import { batchActions } from 'redux-batched-actions';
// import * as Sentry from '@sentry/browser';

import { store } from './store';
import { observable, observeStore } from './utils/events';
import { updatePresenceChannel } from './actions/bookboost/presence';
import {
    setWebsiteKey,
    setDomainToken,
    setApiUrl,
    setPusherInfo,
    setPMConfig, setBrandingDisabled
} from './actions/bookboost/config-actions';
import { saveStateThrottle, getProactiveMessageStorageKey } from './utils/bookboost/storage';
import * as tracker from './actions/bookboost/tracker';
import { conversyEvents } from './utils/bookboost/events';
import { updateHostMinSize } from './utils/bookboost/dom';
import { addMessage } from './actions/conversation';

// Disabled Sentry for now since it catches all errors on our customers websites
// Not only errors from WebMessenger.
// if (process.env.NODE_ENV === 'production') {
//     Sentry.init({ dsn: 'https://a5301cbb1b904d93a0ec40ad28ed3eff@o315477.ingest.sentry.io/5223187' });
// }

let unsubscribeFromStore;
let isInitialized = false;

function onStoreChange(current, previous) {
    const proactiveMessagesChanged = (current.proactiveMessages !== previous.proactiveMessages);
    const widgetStateChanged = (current.widgetState !== previous.widgetState);

    if (proactiveMessagesChanged) {
        const key = getProactiveMessageStorageKey();
        saveStateThrottle(key, current.proactiveMessages);
    }

    if (proactiveMessagesChanged || widgetStateChanged) {
        updateHostMinSize(current.widgetState, current.proactiveMessages);
    }
}

function subscribeToStore() {
    return observeStore(store, (state) => {
        const {
            bbProactiveMessages,
            appState: { widgetState },
            config: { style }
        } = state;
        return {
            proactiveMessages: bbProactiveMessages,
            widgetState,
            displayStyle: style && style.displayStyle,
        };
    }, onStoreChange);
}

function onWidgetOpened() {
    store.dispatch(tracker.conversationOpened());
}

function onWidgetClosed() {
    store.dispatch(tracker.conversationClosed());
}

function addInitialActions(actions) {
    const {
        user: { conversationStarted },
        appState: { activeConversationId },
        conversations,
    } = store.getState();

    const lastUpdatedAt = conversations[activeConversationId].lastUpdatedAt;

    const unixTimeNow = Date.now() / 1000;
    const timeSinceLastUpdate = unixTimeNow - lastUpdatedAt;
    const secondsInMinute = 60;
    const minutesInHour = 60;
    const hoursBeforeShow = 24;

    if (Array.isArray(actions) && actions.length > 0) {
        // Always show at beginning of conversation
        if (!conversationStarted) {
            store.dispatch(
                addMessage(activeConversationId, {
                    role: 'appMaker',
                    type: 'text',
                    actions,
                }),
            );
        // If enough time has elapsed, show after 1,5 seconds in order to let conversation initialise
        } else if (timeSinceLastUpdate > secondsInMinute * minutesInHour * hoursBeforeShow) {
            setTimeout(() => {
                store.dispatch(
                    addMessage(activeConversationId, {
                        role: 'appMaker',
                        type: 'text',
                        actions,
                    }),
                );
            }, 1500);
        }
    }
}


let appId = null;

import * as WebMessenger from './web-messenger';
export * from './web-messenger';

// Overrides WebMessenger.init
export function init(options) {
    const {
        websiteKey,
        domainToken,
        apiUrl,
        pusherInfo,
        initialActions,
        coreOptions = {},
        proactiveMessages = null,
        brandingDisabled,
    } = options;
    isInitialized = true;

    const actions = [];

    if (websiteKey) {
        actions.push(setWebsiteKey(websiteKey));
    } else {
        console.error('No websiteKey provided.');
    }

    if (domainToken) {
        actions.push(setDomainToken(domainToken));
    } else {
       // console.log('No domainToken provided.');
    }

    if (apiUrl) {
        actions.push(setApiUrl(apiUrl));
    } else {
        console.error('No api url provided.');
    }

    if (pusherInfo && pusherInfo.key && pusherInfo.cluster) {
        actions.push(setPusherInfo(pusherInfo));
    } else {
        console.error('Invalid pusherInfo');
    }

    if (proactiveMessages) {
        actions.push(setPMConfig(proactiveMessages));
    }

    if (brandingDisabled != null) {
        actions.push(setBrandingDisabled(brandingDisabled));
    }

    store.dispatch(batchActions(actions));

    appId = coreOptions.appId;

    unsubscribeFromStore = subscribeToStore();

    observable.on('ready', update);
    observable.on('message:sent', update);

    observable.on('widget:opened', onWidgetOpened);
    observable.on('widget:closed', onWidgetClosed);

    observable.on('ready', () => {
        WebMessenger.updateUser({
            properties: {
                domainToken
            }
        });
    });

    observable.on('ready', () => {
        addInitialActions(initialActions);
    });

    window.parent.postMessage('bbReady', '*');

    return WebMessenger.init({
        ...coreOptions,
    });
}

// Overrides WebMessenger.destroy
export function destroy(...args) {
    if (!isInitialized) {
        return;
    }
    isInitialized = false;

    appId = null;

    unsubscribeFromStore();

    observable.off('ready', update);
    observable.off('message:sent', update);

    observable.off('widget:opened', onWidgetOpened);
    observable.off('widget:closed', onWidgetClosed);

    return WebMessenger.destroy(...args);
}

export function update() {
    store.dispatch(updatePresenceChannel(appId));
}

export function track(type, data) {
    store.dispatch(tracker.trackEvent(type, data));
}

function promiseToCallback(promise, callback) {
    if (typeof callback === 'function') {
        promise.then(
            (result) => callback(null, result),
            (error) => callback(error),
        );
    } else {
        promise.catch(
            (error) => console.log('Unhandled command error', error),
        );
    }
}

export function command(type, ...args) {
    switch (type) {
        case 'track':
            // Usage: bb('track', 'EVENT_NAME', [optionalData])
            track(args[0], args[1]);
            break;
        case 'open':
            // Usage: bb('open')
            window.parent.postMessage('bbOpen', '*');
            WebMessenger.open();
            break;
        case 'close':
            // Usage: bb('close')
            window.parent.postMessage('bbClose', '*');
            WebMessenger.close();
            break;
        case 'on':
            // Usage: bb('on', 'EVENT_NAME', handler)
            conversyEvents.on(args[0], args[1]);
            break;
        case 'hideButton':
            // Usage: bb('showButton')
            // Hides the messenger button and sets iframe height and width to 0
            window.parent.postMessage('bbHideButton', '*');
            document.getElementById('messenger-button').style.display = 'none';

            break;
        case 'showButton':
            // Usage: bb('showButton')
            // Shows the messenger button and resets iframe height and width
            window.parent.postMessage('bbShowButton', '*');
            document.getElementById('messenger-button').style.display = 'initial';
            break;
        case 'off':
            // Usage: bb('off', 'EVENT_NAME', handler)
            conversyEvents.off(args[0], args[1]);
            break;
        case 'login': {
            // Usage: bb('login', 'external-id', 'some-jwt', [optionalCallback])
            const [externalId, jwt, callback] = args;
            promiseToCallback(WebMessenger.login(externalId, jwt), callback);
            break;
        }
        case 'logout': {
            // Usage: bb('logout', [optionalCallback])
            const [callback] = args;
            promiseToCallback(WebMessenger.logout(), callback);
            break;
        }
        default:
            console.warn(`${type} is not a bb command`);
            break;
    }
}
